import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { getAuthSession, isAuthenticated, onAuthLogout } from '@/shared/api/client';
import { LayoutRoot } from '@/widgets/app-shell';
import { Skeleton } from '@/shared/ui/Skeleton';

// Lazy-loaded page modules so each route ships in its own chunk.
const LoginPage = lazy(() => import('@/pages/auth/login').then((m) => ({ default: m.LoginPage })));
const SignupPage = lazy(() => import('@/pages/auth/signup').then((m) => ({ default: m.SignupPage })));
const ForgotPage = lazy(() => import('@/pages/auth/forgot').then((m) => ({ default: m.ForgotPage })));
const ResetPage = lazy(() => import('@/pages/auth/reset').then((m) => ({ default: m.ResetPage })));
const BoardsListPage = lazy(() => import('@/pages/boards/list').then((m) => ({ default: m.BoardsListPage })));
const BoardPage = lazy(() => import('@/pages/boards/detail').then((m) => ({ default: m.BoardPage })));
const ProjectsPage = lazy(() => import('@/pages/projects/list').then((m) => ({ default: m.ProjectsPage })));
const ProjectPage = lazy(() => import('@/pages/projects/detail').then((m) => ({ default: m.ProjectPage })));
const WorkgroupsPage = lazy(() => import('@/pages/workgroups').then((m) => ({ default: m.WorkgroupsPage })));
const MyWorkPage = lazy(() => import('@/pages/my-work').then((m) => ({ default: m.MyWorkPage })));
const ProfilePage = lazy(() => import('@/pages/settings/profile').then((m) => ({ default: m.ProfilePage })));
const SecurityPage = lazy(() => import('@/pages/settings/security').then((m) => ({ default: m.SecurityPage })));
const AdminUsersPage = lazy(() => import('@/pages/admin').then((m) => ({ default: m.AdminUsersPage })));

const PageSpinner: React.FC = () => (
  <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
    <Skeleton $h="20px" $w="40%" />
    <Skeleton $h="14px" $w="80%" />
    <Skeleton $h="14px" $w="70%" />
    <Skeleton $h="14px" $w="60%" />
  </div>
);

const AuthGuard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loggedOut, setLoggedOut] = useState(false);

  useEffect(
    () =>
      onAuthLogout(() => {
        setLoggedOut(true);
        navigate('/login', { replace: true, state: { from: location.pathname, expired: true } });
      }),
    [navigate, location.pathname],
  );

  if (loggedOut || !isAuthenticated()) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <Outlet />;
};

const GuestGuard: React.FC = () => {
  if (isAuthenticated()) {
    return <Navigate to="/boards" replace />;
  }
  return <Outlet />;
};

const PATH_TITLES: Array<[prefix: string, title: string]> = [
  ['/boards/', 'Доска'],
  ['/boards', 'Доски'],
  ['/projects/', 'Проект'],
  ['/projects', 'Проекты'],
  ['/workgroups', 'Команды'],
  ['/my-work', 'Моя работа'],
  ['/settings/security', 'Безопасность'],
  ['/settings/profile', 'Профиль'],
  ['/admin', 'Администрирование'],
];

const titleFromPath = (pathname: string) => {
  for (const [prefix, title] of PATH_TITLES) {
    if (pathname.startsWith(prefix)) return title;
  }
  return 'LibreCards';
};

const ProtectedShell: React.FC = () => {
  const location = useLocation();
  const session = getAuthSession();
  if (!session) return <Navigate to="/login" replace />;
  return (
    <LayoutRoot title={titleFromPath(location.pathname)}>
      <Suspense fallback={<PageSpinner />}>
        <Outlet />
      </Suspense>
    </LayoutRoot>
  );
};

const GuestSuspense: React.FC = () => (
  <Suspense fallback={<PageSpinner />}>
    <Outlet />
  </Suspense>
);

export const AppRouter = () => (
  <Routes>
    <Route element={<GuestGuard />}>
      <Route element={<GuestSuspense />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot" element={<ForgotPage />} />
      </Route>
    </Route>

    <Route
      path="/reset"
      element={
        <Suspense fallback={<PageSpinner />}>
          <ResetPage />
        </Suspense>
      }
    />

    <Route element={<AuthGuard />}>
      <Route element={<ProtectedShell />}>
        <Route path="/" element={<Navigate to="/boards" replace />} />
        <Route path="/boards" element={<BoardsListPage />} />
        <Route path="/boards/:id" element={<BoardPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:id" element={<ProjectPage />} />
        <Route path="/workgroups" element={<WorkgroupsPage />} />
        <Route path="/my-work" element={<MyWorkPage />} />
        <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
        <Route path="/settings/profile" element={<ProfilePage />} />
        <Route path="/settings/security" element={<SecurityPage />} />
        <Route path="/admin" element={<AdminUsersPage />} />
      </Route>
    </Route>

    <Route path="*" element={<Navigate to={isAuthenticated() ? '/boards' : '/login'} replace />} />
  </Routes>
);
